import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Info, Coins, Heart, Users, MessageCircle, ArrowLeft, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativePlatform } from '@/services/platformService';
import { CREDIT_PACKAGES } from '@/hooks/useCredits';

const PetMatchingInfo = () => {
  const { t } = useTranslation();
  const isNative = isNativePlatform();
  
  // Helper to get platform-aware price
  const getCreditPrice = (packageKey: keyof typeof CREDIT_PACKAGES) => {
    const pkg = CREDIT_PACKAGES[packageKey];
    return isNative ? pkg.nativePrice : pkg.price;
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8">
          <ArrowLeft className="h-5 w-5" />
          <span>{t('common.backToHome')}</span>
        </Link>
      </div>

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-pet-purple via-pet-pink to-pet-orange relative overflow-hidden">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            
            {/* Left Column: Stats & Benefits */}
            <div className="lg:col-span-2 space-y-8">
              {/* Title for mobile */}
              <div className="lg:hidden text-center mb-8">
                <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
                  PetMatch™
                </h1>
              </div>

              {/* Animated Stats */}
              <div className="grid grid-cols-1 gap-6">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                  <div className="text-4xl font-bold text-white mb-2">AI-Powered</div>
                  <div className="text-white/90 font-medium">Smart Compatibility Matching</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                  <div className="text-4xl font-bold text-white mb-2">1,000+</div>
                  <div className="text-white/90 font-medium">Successful Matches</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                  <div className="text-4xl font-bold text-white mb-2">Safe & Secure</div>
                  <div className="text-white/90 font-medium">Verified Pet Profiles</div>
                </div>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 text-white">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">✓</span>
                  </div>
                  <span className="text-sm font-medium">Real-time matching algorithm</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">✓</span>
                  </div>
                  <span className="text-sm font-medium">Chat directly with pet owners</span>
                </div>
                <div className="flex items-center gap-3 text-white">
                  <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-lg">✓</span>
                  </div>
                  <span className="text-sm font-medium">Simple credit system</span>
                </div>
              </div>
            </div>
            
            {/* Right Column: Value Proposition */}
            <div className="lg:col-span-3 space-y-8">
              {/* Headline - Desktop Only */}
              <div className="hidden lg:block space-y-4">
                <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
                  PetMatch™
                </h1>
                <p className="text-lg md:text-xl text-white/90">
                  {t('matching.subtitle')}
                </p>
              </div>

              <p className="text-lg md:text-xl text-white/90 lg:hidden text-center">
                {t('matching.subtitle')}
              </p>
              {/* How It Works - Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                  <div className="text-3xl mb-3">1️⃣</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{t('matching.step1.title')}</h3>
                  <p className="text-sm text-white/80">{t('matching.step1.description')}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                  <div className="text-3xl mb-3">2️⃣</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{t('matching.step2.title')}</h3>
                  <p className="text-sm text-white/80">{t('matching.step2.description')}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                  <div className="text-3xl mb-3">3️⃣</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{t('matching.step3.title')}</h3>
                  <p className="text-sm text-white/80">{t('matching.step3.description')}</p>
                </div>
              </div>
              {/* Credit System Card */}
              <div className="bg-white rounded-2xl p-6 shadow-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Coins className="h-6 w-6 text-yellow-500" />
                  <h3 className="text-2xl font-bold text-gray-900">{t('matching.credits.title')}</h3>
                </div>
                
                <Alert className="mb-4 bg-blue-50 border-blue-200">
                  <Info className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-900">
                    <strong>{t('credits.welcomeBonus')}</strong> {t('credits.welcomeBonusText')}
                  </AlertDescription>
                </Alert>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border-2 border-blue-200">
                    <Users className="h-8 w-8 mx-auto mb-2 text-blue-600" />
                    <h4 className="font-semibold mb-2 text-gray-900">{t('matching.matchTypes.playdate')}</h4>
                    <p className="text-3xl font-bold text-pet-purple">1</p>
                    <p className="text-sm text-gray-600">{t('credits.credit')}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border-2 border-green-200">
                    <Users className="h-8 w-8 mx-auto mb-2 text-green-600" />
                    <h4 className="font-semibold mb-2 text-gray-900">{t('matching.matchTypes.adoption')}</h4>
                    <p className="text-3xl font-bold text-pet-purple">2</p>
                    <p className="text-sm text-gray-600">{t('credits.credits')}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-xl p-4 text-center border-2 border-pink-200">
                    <Heart className="h-8 w-8 mx-auto mb-2 text-pink-600" />
                    <h4 className="font-semibold mb-2 text-gray-900">{t('matching.matchTypes.breeding')}</h4>
                    <p className="text-3xl font-bold text-pet-purple">4</p>
                    <p className="text-sm text-gray-600">{t('credits.credits')}</p>
                  </div>
                  
                  <div className="bg-gradient-to-br from-yellow-100 to-orange-100 rounded-xl p-4 text-center border-2 border-orange-300 relative overflow-hidden">
                    <Sparkles className="h-8 w-8 mx-auto mb-2 text-orange-600" />
                    <h4 className="font-semibold mb-2 text-gray-900">{t('matching.matchTypes.superMatch')} ⭐</h4>
                    <p className="text-3xl font-bold text-pet-orange">5</p>
                    <p className="text-sm text-gray-600">{t('credits.credits')}</p>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-5 border border-purple-200">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-gray-900">
                    <Coins className="h-5 w-5 text-yellow-600" />
                    {t('credits.packagesTitle')}
                  </h4>
                  <ul className="space-y-2">
                    <li className="flex justify-between items-center py-2 border-b border-purple-200">
                      <span className="font-medium text-gray-700">{t('credits.packages.starter.name')} (10)</span>
                      <span className="font-bold text-green-600 text-lg">${getCreditPrice('starter').toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between items-center py-2 border-b border-purple-200">
                      <span className="font-medium text-gray-700">{t('credits.packages.basic.name')} (25)</span>
                      <span className="font-bold text-green-600 text-lg">${getCreditPrice('basic').toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between items-center py-2 border-b border-purple-200">
                      <span className="font-medium text-gray-700">{t('credits.packages.premium.name')} (50)</span>
                      <span className="font-bold text-green-600 text-lg">${getCreditPrice('premium').toFixed(2)}</span>
                    </li>
                    <li className="flex justify-between items-center py-2">
                      <span className="font-medium text-gray-700">{t('credits.packages.ultimate.name')} (100)</span>
                      <span className="font-bold text-green-600 text-lg">${getCreditPrice('ultimate').toFixed(2)}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Terms & Conditions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-2">
            📜 {t('matching.termsAndConditions.title')}
          </h2>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-pet-purple text-white rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.eligibility')}</h3>
                  <p className="text-gray-700">{t('matching.terms.eligibility.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.creditExpiration')}</h3>
                  <p className="text-gray-700">{t('matching.terms.creditExpiration.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.requestValidity')}</h3>
                  <p className="text-gray-700">{t('matching.terms.matchValidity.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.responsibility')}</h3>
                  <p className="text-gray-700">{t('matching.terms.userResponsibility.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-6 border border-red-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">5</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.refunds')}</h3>
                  <p className="text-gray-700">{t('matching.terms.refundPolicy.description')}</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">6</div>
                <div>
                  <h3 className="font-bold text-lg text-gray-900 mb-2">{t('matching.termsAndConditions.privacy')}</h3>
                  <p className="text-gray-700">{t('matching.terms.privacySafety.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pet-purple to-pet-pink rounded-2xl mb-4 shadow-lg">
              <span className="text-3xl">❓</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
              {t('matching.faq.title')}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find answers to the most common questions about PetMatch™
            </p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 animate-scale-in">
            <Accordion type="single" collapsible className="space-y-3">
              <AccordionItem value="item-1" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-pet-purple font-bold">1</span>
                    <span>{t('matching.faq.q1.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q1.answer')}
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-2" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pet-pink font-bold">2</span>
                    <span>{t('matching.faq.q2.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q2.answer')}
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-3" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">3</span>
                    <span>{t('matching.faq.q3.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q3.answer')}
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="item-4" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-pet-orange font-bold">4</span>
                    <span>{t('matching.faq.q4.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q4.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-bold">5</span>
                    <span>{t('matching.faq.q5.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q5.answer')}
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="border border-gray-200 rounded-xl px-6 py-2 hover:border-pet-purple transition-colors">
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-pet-purple hover:no-underline">
                  <span className="flex items-center gap-3">
                    <span className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-bold">6</span>
                    <span>{t('matching.faq.q6.question')}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 pt-2 pl-11">
                  {t('matching.faq.q6.answer')}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-pet-purple via-pet-pink to-pet-orange">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Find the Perfect Match?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of happy pet owners who have found their perfect companions through PetMatch™
          </p>
          <Link to="/feed">
            <Button size="lg" className="bg-white text-pet-purple hover:bg-gray-100 font-bold px-12 py-6 text-lg shadow-xl hover:scale-105 transition-transform">
              {t('matching.cta')} 🐾
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PetMatchingInfo;